import React from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";

import { BsCheckCircleFill } from "react-icons/bs";
import { IoShieldCheckmark } from "react-icons/io5";
import { FaDatabase } from "react-icons/fa";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { BsIncognito } from "react-icons/bs";
import { FaLock } from "react-icons/fa6";
import { MdDashboardCustomize } from "react-icons/md";
import { GiSettingsKnobs } from "react-icons/gi";
import { TbRouteSquare } from "react-icons/tb";
import { BiFingerprint } from "react-icons/bi";
import { HiArrowLongRight } from "react-icons/hi2";

import { BASE_ASSETS_URL } from "@constants/constants";

import { CalComModalTrigger } from "@components/Modal/CalComModal";
import { CLMButtonPrimary1 } from "components/CLM/v3/Buttons";

const TSC = [
  {
    icon: <IoShieldCheckmark className="w-8 h-8" />,
    title: "Security",
    description:
      "The protection of system resources against unauthorized access.",
    bgColor: "bg-accent-green",
  },
  {
    icon: <FaDatabase className="w-8 h-8" />,
    title: "Availability",
    description:
      "Ensuring systems and information are available for operation and use as committed or agreed.",
    bgColor: "bg-accent-orange",
  },
  {
    icon: <IoCheckmarkDoneSharp className="w-8 h-8" />,
    title: "Processing Integrity",
    description:
      "System processing is complete, valid, accurate, timely and authorized.",
    bgColor: "bg-accent-pink",
  },
  {
    icon: <BsIncognito className="w-8 h-8" />,
    title: "Confidentiality",
    description:
      "Information designated as confidential protected as committed or agreed.",
    bgColor: "bg-primary/30",
  },
  {
    icon: <FaLock className="w-8 h-8" />,
    title: "Privacy",
    description:
      "Personal information is collected, used, retained, disclosed and disposed of in conformity with the commitments in the entity's privacy notice.",
    bgColor: "bg-accent-blue",
  },
];

const complianceRequirements = [
  {
    title: "Custom Security Measures",
    description:
      "Tailor security protocols to fit specific business needs, far exceeding general cloud security standards.",
    icon: <MdDashboardCustomize className="w-10 h-10 stroke-1" />,
    bgColor: "bg-accent-green",
  },
  {
    title: "Direct Control Over Data Access",
    description:
      "Manage who accesses your data and when, with no intermediary.",
    icon: <GiSettingsKnobs className="w-10 h-10 stroke-1" />,
    bgColor: "bg-accent-orange",
  },
  {
    title: "Enhanced Audit Capability",
    description:
      "Maintain comprehensive logs and audit trails that are readily accessible and detailed, exceeding SOC 2 requirements for monitoring and logging.",
    icon: <TbRouteSquare className="w-10 h-10 stroke-1" />,
    bgColor: "bg-accent-pink",
  },
  {
    title: "Access Controls",
    description:
      "You can enforce role-based access control and multi-factor authentication.",
    icon: <BiFingerprint className="w-10 h-10 stroke-1" />,
    bgColor: "bg-accent-blue",
  },
];

const SOC = () => {
  return (
    <div className="space-y-6 sm:space-y-16 max-w-screen-xl mx-auto mb-10 md:mt-20 mt-8 md:px-8 px-4">
      <Head>
        <meta charSet="utf-8" />
        <title>SOC 2 compliant eSignature | BoloSign</title>
        <meta
          name="description"
          content="Electronic signatures refer to signatures in digital format, distinguishing them from traditional handwritten signatures. These digital signatures hold full legal validity for indicating consent or endorsement of document contents. The prevalence of eSignatures is progressively supplanting manual signatures, serving as a versatile method to sign a wide array of documents spanning various sectors on a global scale."
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="Boloforms" />
        <meta name="publisher" content="BoloForms" />
        <meta
          name="keywords"
          content="eSignature, electronic signature, digital signature, eSign, eSignatures, electronic signatures, digital signatures, eSignature software, electronic signature software, digital signature software, eSignature solution, electronic signature solution, digital signature solution, eSignature app, electronic signature app, digital signature app, eSignature platform, electronic signature platform, digital signature platform, eSignature service, electronic signature service, digital signature service, eSignature tool, electronic signature tool, digital signature tool, eSignature online, electronic signature online, digital signature online, eSignature solution online, electronic signature solution online, digital signature solution online, eSignature software online, electronic signature software online, digital signature software online, eSignature platform online, electronic signature platform online, digital signature platform online"
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/soc/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="SOC 2 compliant eSignature | BoloSign"
        />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/soc/"
        />
        <meta
          name="twitter:title"
          content="SOC 2 compliant eSignature | BoloSign"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@boloforms" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta name="twitter:description" content="" />
        <meta property="og:description" content="" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link rel="canonical" href="https://www.bolosign.com/signature/soc/" />
      </Head>

      <section className="space-y-8">
        <div className="font-bold text-4xl sm:text-5xl">
          SOC 2 compliant eSignature
        </div>

        <p className="text-xl sm:text-xl max-w-screen-lg">
          SOC 2 is a set of criteria developed by the American Institute of
          Certified Public Accountants (AICPA) specifically for managing
          customer data based on five &apos;trust service principles&apos;. This
          framework is applicable to any service provider storing customer data
          in the cloud, emphasizing not just physical security but also
          operational and procedural safeguards.
        </p>
      </section>

      <section className="space-y-8 max-w-screen-md">
        <div className="font-bold text-3xl sm:text-4xl">
          Importance of SOC 2 for Service Organizations
        </div>

        <p className="text-xl sm:text-xl max-w-screen-xl">
          Adhering to SOC 2 standards is imperative for ensuring that systems
          are set up to assure security, availability, processing integrity,
          confidentiality and privacy of customer data. Compliance demonstrates
          a company&apos;s commitment to data protection and operational
          excellence, essential for maintaining customer trust and competitive
          advantage.
        </p>
      </section>

      <section>
        <div className="divide-y-2 divide-base-300">
          <div className="space-y-16 py-8 md:py-10">
            <div className="space-y-8 md:space-y-0 md:flex md:items-start md:space-x-8">
              <div className="w-52 h-52 relative flex-shrink-0 flex justify-center items-center bg-gradient-to-t from-neutral-200 to-neutral-100 p-8 rounded-xl ring-2 ring-neutral-500 shadow-xl">
                <Image
                  src={`${BASE_ASSETS_URL}/v2/security/soc1.png`}
                  alt="soc_logo"
                  width={200}
                  height={200}
                />
              </div>

              <div>
                <div className="font-bold text-3xl sm:text-4xl">
                  Types of SOC 2 Reports
                </div>

                <ul className="text-xl xl:leading-8 mt-4 space-y-2">
                  <li className="flex space-x-2">
                    <BsCheckCircleFill className="w-6 h-6 shrink-0 mt-1" />

                    <span>
                      <span className="font-bold">Type I Report </span>{" "}
                      evaluates the design of security processes at a specific
                      point in time.
                    </span>
                  </li>
                  <li className="flex space-x-2">
                    <BsCheckCircleFill className="w-6 h-6 shrink-0 mt-1" />

                    <span>
                      <span className="font-bold">Type II Report</span> assesses
                      how effective those security processes are over a period,
                      typically six months or more providing a more
                      comprehensive validation of compliance.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <div className="font-bold text-3xl sm:text-4xl mb-8">
                Understanding the Trust Service Criteria
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                {TSC.map((item, index) => (
                  <div
                    key={index}
                    className={`py-6 px-8 space-y-3 border border-surface-dark rounded-xl transition-all duration-500 ease-in-out bg-surface-light`}
                  >
                    <div className="flex items-center space-x-2">
                      {item.icon}

                      <p className="text-xl font-semibold">{item.title}</p>
                    </div>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>

              <p className=" max-w-4xl text-xl xl:leading-8 mt-8">
                For detailed guidelines and the official framework, visit{" "}
                <Link
                  className="underline"
                  href="https://www.aicpa-cima.com/"
                  target="_blank"
                >
                  AICPA&apos;s SOC 2
                </Link>{" "}
                details.
              </p>
            </div>
          </div>

          <div className="space-y-16 py-8 md:py-20">
            <div>
              <div className="font-bold text-3xl sm:text-4xl mb-8 max-w-2xl">
                The Necessity of SOC 2 Compliance for eSignature Solutions
              </div>

              <p className="text-2xl font-bold mb-4">
                Data Security Challenges in eSignatures
              </p>
              <p className="max-w-4xl text-xl xl:leading-8 mb-8">
                eSignature technology involves the storage and management of
                highly sensitive data, such as personal identifiers and
                contractual documents. SOC 2 compliance ensures that these data
                handling processes are secure and that integrity and
                confidentiality are maintained.
              </p>

              <p className="text-2xl font-bold mb-4">
                Enhanced Trust with SOC 2 Compliant eSignatures
              </p>
              <p className="max-w-4xl text-xl xl:leading-8">
                Businesses leveraging eSignature technology are often subject to
                scrutiny regarding data security. By implementing a SOC 2
                compliant solution, companies can assure clients and partners of
                their data&apos;s safety, significantly enhancing trust and
                reliability.
              </p>
            </div>
          </div>

          <div className="space-y-16 py-8 md:py-20">
            <div>
              <div className="mb-8">
                <div className="font-bold text-3xl sm:text-4xl max-w-screen-xlg">
                  How does BoloSign support your SOC 2 compliance requirements?
                </div>
                <p className="text-xl xl:leading-8 mt-4">
                  BoloSign&apos;s on-premises solution ensures that you retain
                  full control over your data while meeting the stringent
                  requirements of SOC 2 compliance.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                {complianceRequirements.map((item, index) => (
                  <div
                    key={index}
                    className={`py-6 px-8 space-y-3 border border-surface-dark  rounded-xl transition-all duration-500 ease-in-out bg-surface-light`}
                  >
                    <div className="flex items-center space-x-4">
                      {item.icon}
                      <p className="text-2xl font-semibold">{item.title}</p>
                    </div>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="bg-surface rounded-3xl px-6 py-8 md:px-12 md:py-12 border border-surface-dark">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-8">
            <div className="mx-auto md:mx-0 md:text-left md:w-2/3">
              <div className="font-bold text-3xl sm:text-4xl mb-5">
                How do you enable SOC 2 Compliance in your BoloSign account?
              </div>

              <p className="max-w-4xl text-xl lg:leading-8 text-neutral-600">
                If you&apos;re interested in discussing how BoloSign can
                accommodate your SOC 2 compliance, feel free to reach out to our
                sales team.
              </p>
            </div>

            <div className="mt-8 md:mt-0">
              <CalComModalTrigger>
                <CLMButtonPrimary1
                  buttonText={"Talk to Sales"}
                  icon={<HiArrowLongRight />}
                />
              </CalComModalTrigger>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SOC;
